import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FaFilePdf,
  FaUpload,
  FaTimes,
  FaEye,
  FaRobot,
  FaDna,
  FaChartBar,
  FaExclamationTriangle,
  FaHistory,
  FaSync,
  FaDatabase,
  FaFlask,
  FaStethoscope,
  FaHeartbeat,
  FaUserMd,
  FaClipboardCheck,
  FaInfoCircle,
  FaCloud,
  FaChevronUp,
  FaChevronDown,
  FaDna as FaDnaIcon,
} from "react-icons/fa";
import {
  Button,
  ProgressBar,
  Card,
  Badge,
  Accordion,
  Alert,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import config from "../../../../config";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import axios from "axios";

const ActivitySummary = ({ notifications }) => {
  const { token } = useSelector((state) => state.user);

  const [pending, setPending] = useState([]);
  const [accepted, setAccepted] = useState([]);

  const [userDna, setUserDna] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [geneticHistory, setGeneticHistory] = useState([]);

  const [dnaUploading, setDnaUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [awsStatus, setAwsStatus] = useState(null);
  const [extractionInfo, setExtractionInfo] = useState(null);

  const dnaRef = useRef(null);

  useEffect(() => {
    if (notifications?.length) {
      const pendingNotifications = notifications.filter(
        (n) => n.status === "pending" || n.status === "sent"
      );
      const acceptedNotifications = notifications.filter(
        (n) => n.status === "accepted"
      );
      setPending(pendingNotifications);
      setAccepted(acceptedNotifications);
    }

    fetchUserDna();
    fetchParsedData();
    fetchGeneticHistory();
    checkAwsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, token]);

  // =========================
  // API CALLS
  // =========================
  const checkAwsStatus = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${config.baseURL}/api/dna/aws-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAwsStatus(response.data);
    } catch (error) {
      console.error("Error checking AWS status:", error);
    }
  };

  const fetchUserDna = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${config.baseURL}/api/dna`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserDna(response.data.file);
    } catch (error) {
      console.error("Error fetching DNA file:", error);
    }
  };

  const fetchParsedData = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${config.baseURL}/api/dna/parsed-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // IMPORTANT: store ONLY the API's "data"
        setParsedData(response.data.data);
      } else {
        setParsedData(null);
      }
    } catch (error) {
      console.error("Error fetching parsed data:", error);
      setParsedData(null);
    }
  };

  const fetchGeneticHistory = async () => {
    if (!token) return;

    try {
      const response = await axios.get(
        `${config.baseURL}/api/dna/genetic-history`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setGeneticHistory(response.data.history || []);
    } catch (error) {
      console.error("Error fetching genetic history:", error);
    }
  };

  // =========================
  // UI HELPERS
  // =========================
  const updateProgressBar = (percent) => {
    const progressBar = document.getElementById("analysis-progress");
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
      progressBar.textContent = `${percent}%`;
    }
  };

  const getFileIcon = (fileName) => {
    if (fileName?.toLowerCase().endsWith(".pdf")) {
      return <FaFilePdf size={24} className="text-danger me-2" />;
    }
    return <FaFilePdf size={24} className="text-primary me-2" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // =========================
  // ACTIONS
  // =========================
  const handleParseDNA = async () => {
    if (!userDna || !token) {
      Swal.fire({
        icon: "warning",
        title: "Authentication Required",
        text: "Please log in and ensure DNA file is uploaded.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setParsing(true);

      Swal.fire({
        title: "🧬 AI-Powered Genetic Analysis",
        html: `
          <div style="text-align: center; padding: 20px;">
            <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
            <h5 class="mb-3">Analyzing your genetic report...</h5>
            <div class="progress mb-3" style="height: 10px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 0%" id="analysis-progress">0%</div>
            </div>
            <div class="mt-3 small text-muted">
              <i>AWS Textract: ${
                awsStatus?.aws_configured ? "✅ Available" : "❌ Not configured"
              }</i>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        backdrop: true,
      });

      updateProgressBar(20);

      const response = await axios.post(
        `${config.baseURL}/api/dna/parse-pdf`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 240000,
        }
      );

      Swal.close();

      if (response.data.success) {
        // IMPORTANT: store ONLY the API's "data"
        setParsedData(response.data.data);
        setExtractionInfo(response.data.processing_info || null);
        await fetchGeneticHistory();

        Swal.fire({
          icon: "success",
          title: "✅ Analysis Complete!",
          html: `
            <div style="text-align: left; max-height: 60vh; overflow-y: auto;">
              <h5 class="mb-3">Genetic Report Successfully Analyzed</h5>

              <div class="row mb-3">
                <div class="col-4">
                  <div class="p-2 border rounded text-center">
                    <h4 class="text-primary">${
                      response.data.data.conditions?.length || 0
                    }</h4>
                    <small>Conditions</small>
                  </div>
                </div>
                <div class="col-4">
                  <div class="p-2 border rounded text-center">
                    <h4 class="text-success">${
                      response.data.data.genetic_markers?.length || 0
                    }</h4>
                    <small>Markers</small>
                  </div>
                </div>
                <div class="col-4">
                  <div class="p-2 border rounded text-center">
                    <h4 class="text-warning">${
                      response.data.data.genes_found?.length || 0
                    }</h4>
                    <small>Genes</small>
                  </div>
                </div>
              </div>

              <div class="alert alert-${
                response.data.data.carrier_summary?.carrier_status === "Carrier"
                  ? "warning"
                  : "success"
              }">
                <strong>Carrier Status:</strong> ${
                  response.data.data.carrier_summary?.carrier_status || "Unknown"
                }
                <br>
                <small>${
                  response.data.data.carrier_summary?.carrier_conditions_count ||
                  0
                } carrier condition(s) detected</small>
              </div>

              <div class="mb-3">
                <strong>Extraction Method:</strong> ${
                  response.data.data.extraction_metadata?.method || "Unknown"
                }
                <br>
                <strong>PDF Type:</strong> ${
                  response.data.data.extraction_metadata?.pdf_type || "Unknown"
                }
                <br>
                <strong>Confidence:</strong> ${
                  response.data.data.extraction_metadata?.confidence || "Unknown"
                }
                <br>
                <strong>AWS Textract:</strong> ${
                  response.data.data.extraction_metadata?.aws_textract_used
                    ? "✅ Used"
                    : "❌ Not used"
                }
              </div>
            </div>
          `,
          confirmButtonColor: "#3085d6",
          width: "650px",
        });
      } else {
        throw new Error(response.data.error || "Analysis failed");
      }
    } catch (error) {
      console.error("Error parsing DNA report:", error);
      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Analysis Failed",
        html: `
          <div style="text-align: left;">
            <h5>Failed to analyze DNA report</h5>
            <p class="text-danger"><strong>Error:</strong> ${
              error.response?.data?.error || error.message
            }</p>

            <div class="alert alert-warning mt-3">
              <h6>Possible Issues:</h6>
              <ul class="mb-0">
                <li>PDF might be scanned or encrypted</li>
                <li>Server timeout - try again</li>
                <li>No genetic data found in file</li>
                <li>AWS Textract: ${
                  awsStatus?.aws_configured ? "✅ Configured" : "❌ Not configured"
                }</li>
              </ul>
            </div>
          </div>
        `,
        confirmButtonColor: "#d33",
        width: "500px",
      });
    } finally {
      setParsing(false);
    }
  };

  const handleDnaInputClick = () => dnaRef.current?.click();

  const handleDnaUpload = async (event) => {
    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Authentication Required",
        text: "Please log in again.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      Swal.fire({
        icon: "error",
        title: "Invalid File Type",
        text: "Please select a PDF file",
        confirmButtonColor: "#d33",
      });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "File size must be less than 50MB",
        confirmButtonColor: "#d33",
      });
      return;
    }

    const formData = new FormData();
    formData.append("dnaFile", file);

    try {
      setDnaUploading(true);
      setUploadProgress(0);

      Swal.fire({
        title: "Uploading DNA Report...",
        html: `
          <div style="text-align: center; padding: 20px;">
            <div class="spinner-border text-primary mb-3"></div>
            <p>Uploading: <strong>${file.name}</strong></p>
            <div class="progress mt-3" style="height: 20px;">
              <div id="upload-progress" class="progress-bar" style="width: 0%">0%</div>
            </div>
            <div class="mt-2 small text-muted">
              <i>Max size: 50MB • AWS Textract: ${
                awsStatus?.aws_configured ? "✅ Available" : "❌ Not configured"
              }</i>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
      });

      const response = await axios.post(
        `${config.baseURL}/api/dna/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
            const bar = document.getElementById("upload-progress");
            if (bar) {
              bar.style.width = `${percentCompleted}%`;
              bar.textContent = `${percentCompleted}%`;
            }
          },
        }
      );

      setUserDna(response.data.file);
      setParsedData(null); // reset parsed data on new upload
      setExtractionInfo(null);

      Swal.fire({
        icon: "success",
        title: "Success!",
        html: `
          <div style="text-align: center;">
            <h5>DNA Report Uploaded Successfully!</h5>
            <p>File: <strong>${file.name}</strong></p>
            <p class="text-muted small">Click "AI Parse" to extract genetic information</p>
            <div class="alert alert-info small mt-2">
              <strong>AWS Status:</strong> ${response.data.aws_status || "Unknown"}
            </div>
          </div>
        `,
        confirmButtonColor: "#3085d6",
      });
    } catch (error) {
      console.error("Error uploading DNA report:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        html: `
          <div>
            <p><strong>Failed to upload DNA report</strong></p>
            <p class="text-danger">${
              error.response?.data?.error || "Network error"
            }</p>
            <p class="small text-muted mt-2">Please try again or contact support</p>
          </div>
        `,
        confirmButtonColor: "#d33",
      });
    } finally {
      setDnaUploading(false);
      setUploadProgress(0);
      if (dnaRef.current) dnaRef.current.value = "";
    }
  };

  const handleRemoveDna = async () => {
    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Authentication Required",
        text: "Please log in again.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Remove DNA Report?",
      html: `
        <div style="text-align: left;">
          <p>You are about to remove your DNA report and all associated genetic data.</p>
          <div class="alert alert-danger">
            <strong>Warning:</strong> This action cannot be undone!
          </div>
          <p><strong>File to remove:</strong> ${
            userDna?.originalName || "DNA Report"
          }</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${config.baseURL}/api/dna`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserDna(null);
      setParsedData(null);
      setGeneticHistory([]);
      setExtractionInfo(null);

      Swal.fire({
        icon: "success",
        title: "Removed!",
        text: "DNA report and genetic data removed successfully",
        confirmButtonColor: "#3085d6",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error removing DNA report:", error);
      Swal.fire({
        icon: "error",
        title: "Remove Failed",
        text: "Failed to remove DNA report",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleViewDna = async () => {
    if (!userDna || !token) return;

    try {
      const response = await axios.get(`${config.baseURL}/api/dna/view`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.download = userDna.originalName || "dna_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Error viewing DNA report:", error);
      Swal.fire({
        icon: "error",
        title: "View Failed",
        text: "Failed to view DNA report",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleViewGeneticHistory = async () => {
    if (!token) return;

    try {
      await fetchGeneticHistory();

      Swal.fire({
        title: "🔄 Genetic Data History",
        html: `
          <div style="text-align: left;">
            <p><strong>Total Analysis Versions:</strong> ${
              geneticHistory.length
            }</p>

            <div style="border: 1px solid #ddd; border-radius: 5px; padding: 10px; max-height: 300px; overflow-y: auto;">
              ${
                geneticHistory.length === 0
                  ? '<p style="text-align: center; color: #666; padding: 20px;">No genetic data history found</p>'
                  : geneticHistory
                      .map(
                        (record, index) => `
                        <div style="border-bottom: ${
                          index < geneticHistory.length - 1
                            ? "1px solid #eee"
                            : "none"
                        }; padding: 10px 0;">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Analysis #${
                                geneticHistory.length - index
                              }</strong>
                              ${
                                index === 0
                                  ? ' <span class="badge bg-success">Current</span>'
                                  : ""
                              }
                            </div>
                            <small class="text-muted">${new Date(
                              record.processed_at
                            ).toLocaleDateString()}</small>
                          </div>
                          <div class="mt-2">
                            <small>
                              <span class="badge bg-primary me-1">${
                                record.conditions_count
                              } conditions</span>
                              <span class="badge bg-success me-1">${
                                record.genes_count
                              } genes</span>
                              <span class="badge bg-warning">${
                                record.markers_count
                              } markers</span>
                            </small>
                          </div>
                          <div class="text-muted small mt-1">
                            <i class="fa fa-calendar me-1"></i>${new Date(
                              record.updated_at
                            ).toLocaleString()}
                          </div>
                        </div>
                      `
                      )
                      .join("")
              }
            </div>
          </div>
        `,
        width: "600px",
        confirmButtonColor: "#3085d6",
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Error fetching genetic history:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load history",
        text: "Could not fetch genetic data history",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleRefreshData = async () => {
    await fetchParsedData();
    await fetchGeneticHistory();
    await checkAwsStatus();

    Swal.fire({
      icon: "success",
      title: "Refreshed!",
      text: "Genetic data refreshed successfully",
      timer: 1500,
      showConfirmButton: false,
      position: "top-end",
      toast: true,
    });
  };

  // =========================
  // GENETIC RESULTS (API-ONLY)
  // =========================
  const GeneticAnalysisResults = () => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    if (!parsedData) {
      if (userDna) {
        return (
          <Card className="mt-3 border-info">
            <Card.Body className="text-center py-4">
              <FaFlask size={48} className="text-info mb-3" />
              <h5>Ready for Analysis</h5>
              <p className="text-muted">
                Your DNA report is uploaded and ready for analysis
              </p>
              <Button variant="primary" onClick={handleParseDNA} disabled={parsing}>
                {parsing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FaRobot className="me-2" />
                    AI-Powered Analysis
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        );
      }
      return null;
    }

    const normalizeCondition = (c) => {
      if (typeof c === "string") return { condition_name: c };

      return {
        condition_name: c?.condition_name,
        gene: c?.gene,
        carrier_status: c?.carrier_status ?? c?.status,
        inheritance: c?.inheritance,
        category: c?.category,
        variants: Array.isArray(c?.variants) ? c.variants : [],
        raw: c,
      };
    };

    const conditionsCount = parsedData.conditions?.length || 0;
    const markersCount = parsedData.genetic_markers?.length || 0;
    const genesCount = parsedData.genes_found?.length || 0;
    const genesList = parsedData.genes_found || [];
    const riskFactorsCount = parsedData.risk_factors?.length || 0;

    return (
      <div className="genetic-analysis-results mt-4">
        <Card className="border-primary shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <div>
              <FaDna className="me-2" />
              <strong>Genetic Analysis Results</strong>
              <Badge bg="light" text="dark" className="ms-2">
                API Data
              </Badge>
            </div>
            <div>
              <Button
                variant="outline-light"
                size="sm"
                className="me-2"
                onClick={handleRefreshData}
                title="Refresh Data"
              >
                <FaSync size={12} />
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={handleViewGeneticHistory}
                title="View History"
                className="me-2"
              >
                <FaHistory size={12} />
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                title={isCollapsed ? "Expand" : "Collapse"}
              >
                {isCollapsed ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
              </Button>
            </div>
          </Card.Header>

          {!isCollapsed && (
            <Card.Body>
              {/* Summary Dashboard (counts are API derived) */}
              <div className="dashboard-summary mb-4">
                <Row className="g-3">
                  <Col md={4}>
                    <Card className="text-center border-0 shadow-sm h-100">
                      <Card.Body>
                        <div className="d-flex flex-column align-items-center">
                          <div className="p-3 rounded-circle bg-primary bg-opacity-10 mb-2">
                            <FaStethoscope size={24} className="text-primary" />
                          </div>
                          <h3 className="text-primary mb-0">{conditionsCount}</h3>
                          <small className="text-muted">Conditions</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="text-center border-0 shadow-sm h-100">
                      <Card.Body>
                        <div className="d-flex flex-column align-items-center">
                          <div className="p-3 rounded-circle bg-success bg-opacity-10 mb-2">
                            <FaDna size={24} className="text-success" />
                          </div>
                          <h3 className="text-success mb-0">{genesCount}</h3>
                          <small className="text-muted">Genes</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="text-center border-0 shadow-sm h-100">
                      <Card.Body>
                        <div className="d-flex flex-column align-items-center">
                          <div className="p-3 rounded-circle bg-warning bg-opacity-10 mb-2">
                            <FaExclamationTriangle size={24} className="text-warning" />
                          </div>
                          <h3 className="text-warning mb-0">{riskFactorsCount}</h3>
                          <small className="text-muted">Risk Factors</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>

              {/* Genes Found (API-only) */}
              {genesList.length > 0 && (
                <Card className="mb-4 border-success">
                  <Card.Header className="bg-success text-white">
                    <FaDnaIcon className="me-2" />
                    <strong>Genes Analyzed</strong>
                    <Badge bg="light" text="dark" className="ms-2">
                      {genesCount}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex flex-wrap gap-2">
                      {genesList.map((gene, index) => (
                        <Badge
                          key={index}
                          bg="success"
                          className="p-3 fs-6 d-flex align-items-center"
                        >
                          <FaDna className="me-2" />
                          {gene}
                        </Badge>
                      ))}
                    </div>
                    <small className="text-muted mt-2 d-block">
                      Displayed exactly as returned by the API
                    </small>
                  </Card.Body>
                </Card>
              )}

              {/* Carrier Summary (API-only) */}
              {parsedData.carrier_summary && (
                <Alert
                  variant={
                    parsedData.carrier_summary.carrier_status === "Carrier"
                      ? "warning"
                      : "success"
                  }
                  className="mb-4"
                >
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-3 flex-shrink-0" size={24} />
                    <div className="flex-grow-1">
                      <h5 className="alert-heading mb-1">
                        {parsedData.carrier_summary.carrier_status === "Carrier"
                          ? "🚩 Carrier Status Detected"
                          : "✅ No Carrier Status Detected"}
                      </h5>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="me-3">
                            <strong>Screened:</strong>{" "}
                            {parsedData.carrier_summary.total_conditions_screened ?? "N/A"}
                          </span>
                          <span>
                            <strong>Carrier:</strong>{" "}
                            {parsedData.carrier_summary.carrier_conditions_count ?? "N/A"}
                          </span>
                        </div>
                        <Badge
                          bg={
                            parsedData.carrier_summary.carrier_status === "Carrier"
                              ? "warning"
                              : "success"
                          }
                        >
                          {parsedData.carrier_summary.carrier_status || "Unknown"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Conditions (API-only columns) */}
              {conditionsCount > 0 && (
                <Card className="mb-4">
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <div>
                      <FaStethoscope className="me-2" />
                      <strong>Detected Conditions</strong>
                      <Badge bg="primary" className="ms-2">
                        {conditionsCount}
                      </Badge>
                    </div>
                    <small className="text-muted">Click to expand</small>
                  </Card.Header>

                  <Card.Body className="p-0">
                    <Accordion defaultActiveKey="0">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          View All Conditions ({conditionsCount})
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <div className="table-responsive">
                            <Table hover className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th width="35%">Condition</th>
                                  <th width="15%">Gene</th>
                                  <th width="15%">Status</th>
                                  <th width="15%">Inheritance</th>
                                  {/* <th width="10%">Category</th> */}
                                  <th width="10%">Variants</th>
                                </tr>
                              </thead>

                              <tbody>
                                {(parsedData.conditions || []).map((c, index) => {
                                  const item = normalizeCondition(c);

                                  return (
                                    <tr key={index}>
                                      <td className="text-wrap">
                                        <strong>{item.condition_name || "N/A"}</strong>
                                      </td>

                                      <td>
                                        {item.gene ? (
                                          <Badge bg="secondary" className="text-wrap">
                                            {item.gene}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>

                                      <td>
                                        {item.carrier_status ? (
                                          <Badge bg="info" className="text-wrap">
                                            {item.carrier_status}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>

                                      <td>
                                        {item.inheritance ? (
                                          <span>{item.inheritance}</span>
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td>

                                      {/* <td>
                                        {item.category ? (
                                          <Badge bg="light" text="dark">
                                            {item.category}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted">N/A</span>
                                        )}
                                      </td> */}

                                      <td>
                                        {item.variants?.length ? (
                                          <Badge bg="primary">{item.variants.length}</Badge>
                                        ) : (
                                          <span className="text-muted">0</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card.Body>
                </Card>
              )}

              {/* Risk Factors (API-only) */}
              {riskFactorsCount > 0 && (
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <FaChartBar className="me-2" />
                    <strong>Risk Factors</strong>
                    <Badge bg="warning" className="ms-2">
                      {riskFactorsCount}
                    </Badge>
                  </Card.Header>

                  <Card.Body>
                    {(parsedData.risk_factors || []).map((risk, index) => (
                      <Alert
                        key={index}
                        variant={
                          risk.risk_score === "High"
                            ? "danger"
                            : risk.risk_score === "Medium"
                            ? "warning"
                            : "info"
                        }
                        className="d-flex align-items-start"
                      >
                        <div className="flex-shrink-0 me-3">
                          <FaExclamationTriangle size={20} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div>
                              <strong>{risk.associated_condition || "Risk Factor"}</strong>
                              {risk.description ? (
                                <div className="small text-muted">{risk.description}</div>
                              ) : null}
                            </div>
                            {risk.risk_score ? (
                              <Badge
                                bg={risk.risk_score === "High" ? "danger" : "warning"}
                              >
                                {risk.risk_score} Risk
                              </Badge>
                            ) : null}
                          </div>

                          {risk.recommendation ? (
                            <div className="mt-2 p-2 bg-white bg-opacity-25 rounded">
                              <small>
                                <FaClipboardCheck className="me-1" />
                                <strong>Recommendation:</strong> {risk.recommendation}
                              </small>
                            </div>
                          ) : null}
                        </div>
                      </Alert>
                    ))}
                  </Card.Body>
                </Card>
              )}

              {/* Recommendations (API-only) */}
              {Array.isArray(parsedData.recommendations) &&
                parsedData.recommendations.length > 0 && (
                  <Card className="border-info">
                    <Card.Header className="bg-info text-white">
                      <FaUserMd className="me-2" />
                      <strong>Medical Recommendations</strong>
                    </Card.Header>
                    <Card.Body>
                      <ul className="list-unstyled mb-0">
                        {parsedData.recommendations.map((rec, index) => (
                          <li key={index} className="mb-2">
                            <div className="d-flex">
                              <div className="flex-shrink-0 me-3">
                                <FaHeartbeat className="text-info mt-1" />
                              </div>
                              <div className="flex-grow-1">{rec}</div>
                            </div>
                            {index < parsedData.recommendations.length - 1 && (
                              <hr className="my-2" />
                            )}
                          </li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                )}

              {/* Additional Findings (API-only) */}
              {Array.isArray(parsedData.additional_findings) &&
                parsedData.additional_findings.length > 0 && (
                  <Card className="border-warning mt-4">
                    <Card.Header className="bg-warning text-white">
                      <FaInfoCircle className="me-2" />
                      <strong>Additional Findings</strong>
                    </Card.Header>
                    <Card.Body>
                      {parsedData.additional_findings.map((finding, index) => (
                        <div key={index} className="mb-3 pb-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{finding.type || "Finding"}</strong>
                              {finding.gene ? (
                                <Badge bg="secondary" className="ms-2">
                                  {finding.gene}
                                </Badge>
                              ) : null}
                            </div>

                            {finding.significance ? (
                              <Badge bg="warning">{finding.significance}</Badge>
                            ) : (
                              <Badge bg="secondary">Unknown</Badge>
                            )}
                          </div>

                          {finding.description ? (
                            <p className="mb-1 mt-2">{finding.description}</p>
                          ) : null}
                          {finding.notes ? (
                            <small className="text-muted">{finding.notes}</small>
                          ) : null}
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}

              {/* Metadata Footer (API-only) */}
              <div className="mt-3 p-2 bg-light rounded small">
                <div className="d-flex justify-content-between">
                  <div>
                    <FaDatabase className="me-1" />
                    <strong>Analysis Info:</strong>
                    <span className="ms-2">
                      Report ID: {parsedData.raw_extraction_info?.report_id ?? "N/A"}
                    </span>
                    <span className="ms-3">
                      Lab: {parsedData.raw_extraction_info?.lab ?? "Unknown"}
                    </span>
                  </div>
                  <div>
                    {parsedData.database_info?.processed_at ? (
                      <span>
                        Analyzed:{" "}
                        {new Date(parsedData.database_info.processed_at).toLocaleDateString()}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="mt-1">
                  <FaCloud className="me-1" />
                  <strong>Processing:</strong>
                  <span className="ms-2">
                    Confidence: {parsedData.extraction_metadata?.confidence ?? "Unknown"}
                  </span>
                </div>
              </div>
            </Card.Body>
          )}
        </Card>
      </div>
    );
  };

  // =========================
  // UPLOAD SECTION
  // =========================
  const DnaUploadSection = () => (
    <div className="dna_upload_section mt-4 mb-4">
      <h4>
        <FaDna className="me-2" />
        DNA Report Analysis
      </h4>

      <div className="dna_upload_wrapper">
        <input
          type="file"
          id="dnaUpload"
          style={{ display: "none" }}
          accept=".pdf,.csv"
          ref={dnaRef}
          onChange={handleDnaUpload}
          disabled={dnaUploading || !token}
        />

        {userDna ? (
          <Card className="border-success shadow-sm">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {getFileIcon(userDna.originalName)}
                  <div>
                    <div className="dna-filename fw-bold">
                      {userDna.originalName || userDna.fileName || "DNA Report"}
                    </div>
                    <div className="text-muted small">
                      <span className="me-3">
                        <FaUpload className="me-1" />
                        Uploaded:{" "}
                        {userDna.uploadedAt
                          ? dayjs(userDna.uploadedAt).format("MMM D, YYYY")
                          : "N/A"}
                      </span>
                      {userDna.fileSize ? (
                        <span>
                          <FaDatabase className="me-1" />
                          Size: {formatFileSize(userDna.fileSize)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="dna-actions">
                  {userDna.originalName?.toLowerCase().endsWith(".pdf") && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="me-2 mt-1"
                      onClick={handleParseDNA}
                      disabled={parsing || !token}
                    >
                      {parsing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <FaRobot className="me-1" />
                          AI Parse
                        </>
                      )}
                    </Button>
                  )}

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-2 mt-1"
                    onClick={handleDnaInputClick}
                    disabled={dnaUploading || !token}
                    title="Update File"
                  >
                    <FaUpload className="me-1" />
                    Update
                  </Button>

                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-2 mt-1"
                    onClick={handleViewDna}
                    disabled={dnaUploading || !token}
                    title="View PDF"
                  >
                    <FaEye className="me-1" />
                    View
                  </Button>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-1"
                    onClick={handleRemoveDna}
                    disabled={dnaUploading || !token}
                    title="Remove File"
                  >
                    <FaTimes />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card className="text-center border-dashed py-5">
            <Card.Body>
              <div className="mb-3">
                <FaUpload size={48} className="text-muted" />
              </div>
              <h5>Upload DNA Report</h5>
              <p className="text-muted mb-3">
                Upload your genetic report PDF for AI-powered analysis
              </p>
              <Button
                variant="primary"
                onClick={handleDnaInputClick}
                disabled={dnaUploading || !token}
                className="px-4"
              >
                {dnaUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    Choose PDF File
                  </>
                )}
              </Button>
              <div className="mt-3">
                <small className="text-muted">
                  Supported formats: PDF (Max 50MB)
                </small>
                {!token && (
                  <div className="text-warning small mt-2">
                    <FaExclamationTriangle className="me-1" />
                    Authentication required
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {dnaUploading && (
          <div className="mt-3">
            <div className="d-flex justify-content-between mb-1">
              <small>Uploading...</small>
              <small>{uploadProgress}%</small>
            </div>
            <ProgressBar now={uploadProgress} variant="success" animated className="w-100" />
          </div>
        )}
      </div>

      {/* Show Genetic Analysis Results */}
      <GeneticAnalysisResults />

      {/* No Data Message with Upload */}
      {userDna && !parsedData && (
        <Card className="mt-3 border-info">
          <Card.Body className="text-center py-4">
            <FaFlask size={48} className="text-info mb-3" />
            <h5>Ready for Analysis</h5>
            <p className="text-muted">
              Your DNA report is uploaded and ready for analysis
            </p>
            <Button
              variant="primary"
              onClick={handleParseDNA}
              disabled={parsing}
              className="px-4"
            >
              {parsing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <FaRobot className="me-2" />
                  Start AI-Powered Analysis
                </>
              )}
            </Button>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  return (
    <div className="card-lite p-3">
      <DnaUploadSection />
    </div>
  );
};

export default ActivitySummary;